<script lang="ts" setup>
  import { ref, computed, watch, nextTick, toRefs } from 'vue';
  import { useNotificationsStore, type NotificationItem } from '@/common/store/pinia/notifications';
  import { isFunction } from 'lodash-es';
  import type { ElNotification } from '@uikit/ui-kit/types/notification';
  import useNotification from '@/common/composables/useNotification';
  import More from '@/common/shared/More.vue';

  type Props = {
    notification: NotificationItem
  }

  const props = defineProps<Props>();

  const { notification } = toRefs(props);
  const notificationsStore = useNotificationsStore();

  const moreRef = ref<InstanceType<typeof More> | null>(null);

  const textRowCount = computed(() => moreRef.value?.textRowCount || 0);

  const { moreThanMaxRows, actionButtons } = useNotification({
    notification,
    textRowCount,
  });

  const routeActions = computed(() => actionButtons.value?.filter((action) => 'route' in action) || []);
  const clickActions = computed(() => actionButtons.value?.filter(action => !('route' in action)) || []);

  const showElements = computed(() => !!actionButtons.value?.length || !!notification.value.components?.length);
  const withProgressBar = computed(() => notification.value.progress !== undefined && notification.value.progress !== null);

  const notificationRef = ref<InstanceType<typeof ElNotification> | null>(null);

  watch(() => notification.value.duration, async (newVal) => {
    if (newVal) {
      await nextTick();
      notificationRef.value?.startTimer();
    }
  });

  const close = () => {
    if (!notification.value.id) return;

    notificationsStore.dismiss(notification.value.id);

    if (isFunction(notification.value.onClose)) {
      notification.value.onClose();
    }
  };

  defineExpose({
    close
  });
</script>

<template>
  <el-notification-component
    ref="notificationRef"
    class="notification-item v-bottom-notification"
    visible
    :data-operationid="notification.operationId"
    :data-testid="notification.type"
    :duration="notification.duration"
    :showClose="notification.showClose"
    :type="notification.type"
    @update:visible="close"
  >
    <div class="title">
      {{ notification.title }}
    </div>
    <el-progress
      v-if="withProgressBar"
      animate
      class="my-8"
      :percentage="notification.progress"
    />
    <div
      v-if="notification.message"
      :class="{
        'el-text--weight-bold': withProgressBar,
      }"
    >
      <more
        ref="moreRef"
        :text="notification.message"
        :hide-toggle="moreThanMaxRows"
      />
    </div>
    <div
      v-if="showElements"
      class="notification-elements"
    >
      <div
        v-if="actionButtons?.length"
        class="actions"
      >
        <el-button
          v-for="actionButton in clickActions"
          :key="actionButton.label"
          type="ghost"
          :data-testid="actionButton.name"
          @click="actionButton.click"
        >
          {{ actionButton.label }}
        </el-button>
        <router-link
          v-for="actionButton in routeActions"
          :key="actionButton.label"
          v-slot="{ navigate }"
          :to="actionButton.route"
          custom
        >
          <el-button
            type="ghost"
            :data-testid="actionButton.name"
            @click="navigate"
          >
            {{ actionButton.label }}
          </el-button>
        </router-link>
      </div>
      <template v-if="notification.components?.length">
        <component
          :is="component.name"
          v-for="(component, index) in notification.components"
          :key="`${index}-${notification.id}`"
          class="mr-8"
          v-bind="component.attrs"
          v-on="component.listeners"
        >
          {{ component.content }}
        </component>
      </template>
    </div>
  </el-notification-component>
</template>

<style lang="scss" scoped>
  .actions {
    display: flex;
    flex-direction: row;
  }

  .notification-item {
    width: 408px;
    font-size: $av-font-size-xs;
    z-index: $av-z-index-notifications;
  }

  .notification-elements {
    display: flex;
    margin-left: -8px;
    margin-top: 8px;
    flex-direction: row;
  }

  .title {
    font-weight: $av-font-weight-bold;
    word-break: break-word;
  }

  ::v-deep .el-notification__icon {
    display: flex;
    align-items: start;
    padding-top: 4px;
  }
</style>
